'use client';
import React, { useState, useEffect } from 'react';

import { useReactiveVar, useSuspenseQuery } from '@apollo/client';
import { useSearchParams } from 'next/navigation';

import usePageVisualizationEvent from '@storefront/app/analytics/hooks/usePageVisualizationEvent';
import { sendAnalytics } from '@storefront/app/analytics/SimpleTriggerEvent';
import {
  GetSkuProductsDocument,
  GetSkuProductsQuery,
} from '@storefront/data-commerce';
import useCheckViewSize from '@storefront/hooks/useCheckViewSize';
import useUdlStore from '@storefront/hooks/useUdl';
import useVisibilityObserver from '@storefront/hooks/useVisibilityObserver';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import udlEvents from '@storefront/services/udl/udlEvents';
import InsideData from '@storefront/shared-components/inside-data/inside-data';

import {
  applyPreselectedPlansToState,
  handleOutOfStock,
} from './product-configuration.utils';
import { ScrollProvider } from './scroll-context';
import { BundleProductWithLowestPrice } from '../product-configuration/product-configuration.utils';
import { configurationState } from '../state-management/pdp-config-state';
import { getSkuList, loadProductConfiguration } from '../utils/utils';
import MainProduct from './main-product/main-product';
import RenderNotRequiredOptions from './not-required-options/render-not-required-options';
import RenderRequiredOptions from './required-required-options/render-required-options';

export const ProductConfiguration = () => {
  const [loadingImage, setLoadingImage] = useState(true);
  const [pagePlanEventSent, setPagePlanEventSent] = useState(false);
  const [addPaperEventSent, setAddPaperEventSent] = useState(false);

  const configuration = useReactiveVar(configurationState);
  const searchParams = useSearchParams();
  const { isMobile, isTablet } = useCheckViewSize();
  const udlStore = useUdlStore();
  const skuList = getSkuList(searchParams?.get('skus'));
  const activeSKU = configuration.activeSku as string;
  const preSelectedSku = searchParams?.get('selectedSku');
  const preSelectedPrintPlan = searchParams?.get('printPlan');
  const preSelectedPaper = searchParams?.get('paper');

  const pdpFlow = searchParams?.get('pdpFlow');
  const isOptionSelected = !!activeSKU;

  const {
    data: { products },
  } = useSuspenseQuery<GetSkuProductsQuery>(GetSkuProductsDocument, {
    variables: { skuList },
  });

  const multipleProduct = products?.items as BundleProductWithLowestPrice[];
  const bundleProduct = multipleProduct?.find(
    (product) => product.sku === configuration.activeSku
  );

  usePageVisualizationEvent({
    simpleUiEvent: uiEvents.printerConfigurationPageSD,
    override: {
      actionAuxParams: `selected_sku=${activeSKU}&pdp_flow=${pdpFlow}
    `,
      coreEvent: true,
    },
  });

  const isChooseYourPagePlanVisible = useVisibilityObserver(
    'options-modal-container',
    0.1
  )?.isVisible;
  const isAddPaperVisible = useVisibilityObserver(
    'add-paper-section',
    0.1
  )?.isVisible;

  useEffect(() => {
    if (isChooseYourPagePlanVisible && !pagePlanEventSent) {
      sendAnalytics({
        simpleUiEvent: {
          ...uiEvents.printerConfigurationPageSD,
          screenMode: 'ChooseYourPagePlan',
        },
      });
      setPagePlanEventSent(true);
    }

    if (isAddPaperVisible && !addPaperEventSent) {
      sendAnalytics({
        simpleUiEvent: {
          ...uiEvents.addPaperDisplayed,
          screenMode: 'AddPaper',
        },
      });
      setAddPaperEventSent(true);
    }
  }, [isChooseYourPagePlanVisible, isAddPaperVisible]);

  useEffect(() => {
    const savedState = loadProductConfiguration() ?? {};
    if (savedState.activeSku) {
      if (!skuList.includes(savedState.activeSku as string)) {
        savedState.activeSku = '';
      }
    }
    if (preSelectedSku) {
      savedState.activeSku = preSelectedSku;
    }
    applyPreselectedPlansToState({
      savedState,
      multipleProduct,
      preSelectedPrintPlan,
      preSelectedPaper,
    });
    configurationState(savedState);
  }, [products]);

  useEffect(() => {
    udlStore.trackEvent(udlEvents.getEventByID('pdpConfigView'));
  }, []);

  handleOutOfStock(activeSKU, multipleProduct, configuration);

  const shouldShowSmallerImage = isMobile || isTablet;

  return (
    <>
      <div id="printer-selection-container">
        <ScrollProvider>
          <MainProduct
            showSmallImage={shouldShowSmallerImage}
            loadingImage={loadingImage}
            setLoadingImage={setLoadingImage}
            skuList={skuList}
          />
          <RenderRequiredOptions
            showSmallImage={shouldShowSmallerImage}
            loadingImage={loadingImage}
            setLoadingImage={setLoadingImage}
            skuList={skuList}
            isOptionSelected={isOptionSelected}
          />
          <RenderNotRequiredOptions
            showSmallImage={shouldShowSmallerImage}
            loadingImage={loadingImage}
            setLoadingImage={setLoadingImage}
            skuList={skuList}
          />
        </ScrollProvider>
      </div>
      <InsideData
        page={{ name: 'pdp-config', type: 'product' }}
        product={bundleProduct}
      />
    </>
  );
};

export default ProductConfiguration;
